import React, {
  useContext, useEffect, useState, useMemo, useRef,
} from 'react'
import { Title } from '@usap/widgets'
import { LazyLoad, Box, ResponsiveBox } from '@usap/uicomponents'
import RecommendationSkeletonLoader from '@usap/uicomponents/src/components/loaders/RecommendationSkeletonLoader'
import styled from 'styled-components'
import axios from 'axios'
import { LocalContext } from '../../../helpers/useLocalContext'
import getInventoryDetails from '../../../helpers/getInventoryDetails'
import { GTM } from '../../../helpers'
import RecommendationViewCardLoadable from '../../Loadables/RecommendationViewCard.loadable'

import useIsMounted from '../../../../hooks/useIsMounted'
import apiConfig from '../../../../config/api-config'
import { Carousel } from '../../Carousel/Carousel'
import getXCtrValueString from '../../../helpers/getXCtrValueString'
import { ItemWrapper } from './Recommendation.css'
import {
  RecommendationWidgetNameEnum,
  useGetProductListingName,
} from '../../../hooks/useGetProductListingName'

import { getSyncSEDetails } from '../../../helpers/getShippingExpectationDate'
import { useLocationContext } from '../../../contexts/location.context'

const MainContainer = styled(ResponsiveBox)`
  padding: 0 1rem;
  max-width: 1440px;

  @media (min-width: 600px) {
    padding: 0 1.25rem;
  }
  @media (min-width: 768px) {
    padding: 0 1.75rem;
  }
`
const RecommendationTitle = styled(Title)`
  line-height: 1.875rem;
  font-weight: 500;
  font-size: 1.25rem;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    line-height: 2.25rem;
    font-weight: 600;
    font-size: 1.5rem;
  }
`

const styleCSS = {
  pad: { horizontal: 'small', vertical: 'medium' },
  margin: { horizontal: 'auto' },
  fxstyle: {
    other: { minWidth: '300px', overflowY: 'auto' },
    large: {},
  },
  fontsize: { fontSize: '20px' },
}

const RecomWrapper = styled.div`
  background-color: ${({ bgColor }) => bgColor};
  padding-top: 1.5rem;
  padding-bottom: ${({ lastRecord }) => (lastRecord ? '2rem' : '')};

  @media (min-width: 768px) {
    padding-top: 2rem;
  }
`

export default function Recommendations({
  label = '',
  widgetName,
  recommendationUrl,
  showAddToCart,
  showGuaranteedToFit,
  recommendationData,
  bgColor = '#fff',
  lastRecord = false,
}) {
  const { isBot } = useContext(LocalContext)
  const [records, setRecords] = useState([])
  const [shippingExp, setShippingExp] = useState({})
  const isMounted = useIsMounted()
  const { viewItemListGTM } = GTM
  const locationState = useLocationContext()
  const zipCode = (locationState || {}).postalCode
  const prevZipRef = useRef(null)

  useEffect(() => {
    axios
      .get(recommendationUrl, {
        headers: {
          apiKey: apiConfig.apiKey,
          'x-ctr-value': getXCtrValueString(),
        },
      })
      .then(res => {
        setRecords(Array.isArray(res?.data) ? [...res.data] : [])
      })
      .catch(error => {
        setRecords([])
        console.error('error getting recommendations', widgetName, error)
      })
  }, [recommendationUrl, widgetName])

  const pids = useMemo(
    () => (Array.isArray(records) ? [...records] : [])
      .map(item => item.id)
      .join('|'),
    [records],
  )

  const currentItemListName = useGetProductListingName({
    widgetName: RecommendationWidgetNameEnum?.[widgetName],
  })

  const formatWidgetNameIds = str => {
    if (typeof str !== 'string') return ''
    return str.replace(/\s+/g, '_').toLowerCase()
  }

  const formattedName = useMemo(() => formatWidgetNameIds(widgetName), [widgetName])

  useEffect(() => {
    if (!zipCode || !records.length) return
    if (prevZipRef.current === zipCode && shippingExp?.length) return
    prevZipRef.current = zipCode

    const seChunks = []
    for (let i = 0; i < records.length; i += 6) {
      seChunks.push(records.slice(i, i + 6))
    }
    Promise.all(seChunks.map(chunk => getSyncSEDetails(chunk, true, undefined, zipCode)))
      .then(results => setShippingExp(Object.assign({}, ...results)))
      .catch(err => console.error('error re-fetching SE on zip change', err?.message || err))
  }, [zipCode, records, shippingExp?.length])

  useEffect(() => {
    window.__GTM_IMP = typeof window.__GTM_IMP === 'undefined' ? {} : window.__GTM_IMP

    // eslint-disable-next-line no-negated-condition
    if (typeof window.__GTM_IMP !== 'undefined' && typeof window.__GTM_IMP[`${formattedName}`] === 'undefined' && records.length > 0) {
      if (!isMounted()) return
      const seChunks = []
      for (let i = 0; i < records.length; i += 6) {
        seChunks.push(records.slice(i, i + 6))
      }
      Promise.all([
        getInventoryDetails(pids, false),
        Promise.all(seChunks.map(chunk => getSyncSEDetails(chunk))).then(results => Object.assign({}, ...results)),
      ])
        .then(([invDetails, seData]) => {
          const newRecords = records.map(record => ({
            ...record,
            inventory:
              records.length > 1 ? invDetails[record.id] : invDetails,
          }))
          setShippingExp(seData)
          setRecords(newRecords)
          viewItemListGTM({
            listName: currentItemListName,
            widgetName:
              RecommendationWidgetNameEnum?.[widgetName] || label || '',
            products: newRecords,
          })

          window.__GTM_IMP[`${formattedName}`] = true
        })
        .catch(error => {
          console.error(
            'error getting reco inventory and delivery estimate details',
            error?.message || error,
          )
          viewItemListGTM({
            listName: currentItemListName,
            widgetName:
              RecommendationWidgetNameEnum?.[widgetName] || label || '',
            products: records,
          })
          window.__GTM_IMP[`${formattedName}`] = true
        })
    }
  }, [records, isMounted, pids, formattedName, currentItemListName, label, viewItemListGTM, widgetName])

  return (
    <RecomWrapper bgColor={bgColor} lastRecord={lastRecord}>
      <LazyLoad once height={400} offset={100} isBot={isBot}>
        <MainContainer
          id={formattedName}
          pad={styleCSS.pad}
          margin={styleCSS.margin}
          data-cnstrc-recommendations
          data-cnstrc-recommendations-pod-id={recommendationData.podId}
          data-cnstrc-num-results={records?.length}
          data-cnstrc-result-id={records?.[0]?.resultId}
          data-recid={label}
        >
          {records?.length ? (
            <>
              <Box id={`${formatWidgetNameIds(widgetName)}_Heading`}>
                <RecommendationTitle type="h2" label={label} />
              </Box>

              <Carousel id={`recom-${widgetName}`}>
                {records &&
                  records.map((record, index) => (
                    <ItemWrapper
                      aria-label="Item Wrapper"
                      key={`item-${record.id}`}
                      data-cnstrc-item="recommendation"
                      data-cnstrc-item-id={record?.id}
                      data-cnstrc-item-name={record?.title}
                      data-cnstrc-strategy-id={record?.strategyId}
                      data-cnstrc-item-price={record?.pricing?.regularPrice}
                    >
                      <RecommendationViewCardLoadable
                        data={record}
                        key={record.id}
                        index={index}
                        widgetName={widgetName}
                        showAddToCart={showAddToCart}
                        showGuaranteedToFit={showGuaranteedToFit}
                        label={label}
                        shippingExp={shippingExp}
                      />
                    </ItemWrapper>
                  ))}
              </Carousel>
            </>
          ) : (
            <RecommendationSkeletonLoader />

          )}
        </MainContainer>
      </LazyLoad>
    </RecomWrapper>
  )
}
